import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faSpinner } from "@fortawesome/free-solid-svg-icons";
import useChat from "../../stores/useChat";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContainer = styled.div`
  background: #ffffff;
  border-radius: 6px;
  width: 100%;
  max-width: 450px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  position: relative;
  box-sizing: border-box;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #999;
  cursor: pointer;
  font-size: 18px;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;

  &:hover {
    color: #333;
  }
`;

const InputGroup = styled.div`
  margin-bottom: 24px;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  font-size: 15px;
  border: 1px solid #eaeaea;
  border-radius: 6px;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.2s;

  &:focus {
    border-color: #ff8f40;
  }

  &:disabled {
    background-color: #f9f9f9;
    cursor: not-allowed;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const CancelButton = styled(Button)`
  background: #ffffff;
  border: 1px solid #eaeaea;
  color: #666;

  &:hover:not(:disabled) {
    background: #f5f5f5;
  }
`;

const ConfirmButton = styled(Button)`
  background: #ff8f40;
  border: 1px solid #ff8f40;
  color: #ffffff;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;

const TelegramModal = ({ isOpen, onClose }) => {
  const { telegramToken, setTelegramToken } = useChat();
  const [newToken, setNewToken] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setNewToken(telegramToken || "");
    }
  }, [isOpen, telegramToken]);

  if (!isOpen) return null;

  const isValidTelegramToken = (token) => /^[0-9]+:[a-zA-Z0-9_-]{35,}$/.test(token);

  const handleConfirm = async () => {
    if (!newToken.trim()) {
      toast.warning("Por favor, insira o token do Telegram.");
      return;
    }

    if (!isValidTelegramToken(newToken)) {
      toast.error("O token fornecido não é válido. Verifique e tente novamente.");
      return;
    }

    setLoading(true);
    try {
      await setTelegramToken(newToken);
      toast.success("Token do Telegram adicionado com sucesso!");
      onClose();
    } catch (error) {
      console.error("Erro ao adicionar o token do Telegram:", error);
      toast.error("Ocorreu um erro ao adicionar o token. Tente novamente mais tarde.");
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !loading) {
      onClose();
    }
  };

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>Configurar Telegram</ModalTitle>
          <CloseButton onClick={onClose} disabled={loading}>
            <FontAwesomeIcon icon={faTimes} />
          </CloseButton>
        </ModalHeader>

        <InputGroup>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#666' }}>
            Token da API (BotFather)
          </label>
          <StyledInput
            type="text"
            placeholder="Ex: 123456789:ABCDefGhIJKlmNoPQRstUVwxyZ"
            value={newToken}
            onChange={(e) => setNewToken(e.target.value)}
            disabled={loading}
            autoFocus
          />
        </InputGroup>

        <ModalFooter>
          <CancelButton onClick={onClose} disabled={loading}>
            Cancelar
          </CancelButton>
          <ConfirmButton onClick={handleConfirm} disabled={loading}>
            {loading && <FontAwesomeIcon icon={faSpinner} spin />}
            Confirmar
          </ConfirmButton>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default TelegramModal;